import { Box } from '@material-ui/core';
import dayjs from 'dayjs';
import { Form, Formik } from 'formik';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../../../Session';
import {
    SaleTransactionStage,
    FullSaleTransactionDataFragment,
    UpdateSaleTransactionMutationVariables,
    SettingValueUnit,
    UserType,
    useUpdateSaleTransactionMutation,
    NameOnlyFileDataFragment,
    useUploadSalesTransactionHandoverAttachmentMutation,
    useRemoveSalesTransactionHandoverAttachmentMutation,
    useUploadVsaAttachmentMutation,
    useRemoveVsaAttachmentMutation,
    useUploadSalesTransactionPhotoMutation,
    useRemoveSalesTransactionPhotoMutation,
    useUpdateSalesTransactionFrontPagePhotoMutation,
    useUpdateBiddingMutation,
    useAddSalesTransactionVehicleDiagramCommentMutation,
    useRemoveSalesTransactionVehicleDiagramCommentMutation,
} from '../../../api';
import FileViewerProvider from '../../../components/FileViewerProvider';
import useBasicStyle from '../../../layouts/BasicLayout/useBasicStyles';
import { diffUploads } from '../../../utilities/file';
import { useHandleError } from '../../../utilities/handleErrors';
import useHandoverLocationOptions, { defaultOtherLocationOption } from '../../../utilities/useHandoverLocationOptions';
import useValidator from '../../../utilities/useValidator';
import validators from '../../../utilities/validators';
import { VehicleDiagramComment } from '../components/VehicleDiagram';
import EditSaleTransactionBody from './EditSaleTransactionBody';
import EditSaleTransactionHeader from './EditSaleTransactionHeader';

export type EditSaleTransactionProps = {
    saleTransaction: FullSaleTransactionDataFragment;
    goToView: () => void;
};

export type EditSaleTransactionFormValues = {
    handover: {
        targetHandoverDate: Date;
        targetHandoverTime: Date;
        handoverLocationField: { main: string; other?: string };
        attachments: (NameOnlyFileDataFragment | File)[];
        vsaAttachments: (NameOnlyFileDataFragment | File)[];
    };
    photos: (NameOnlyFileDataFragment | File)[];
    frontPagePhoto?: NameOnlyFileDataFragment | File;
    biddingSession?: {
        name: string;
        notes: string;
        urgent: boolean;
        startPeriod: Date | null;
        endPeriod: Date | null;
    };
    comments?: VehicleDiagramComment[];
} & UpdateSaleTransactionMutationVariables['fields'];

const computeInitialValues = (
    values: FullSaleTransactionDataFragment,
    handoverLocationOptions: Array<{ value: string; label: string }>
): EditSaleTransactionFormValues => {
    const {
        id,
        stage,
        createdAt,
        createdBy,
        updatedAt,
        updatedBy,
        purchaseTransaction,
        latestBiddingSession,
        latestBiddingSessionEntry,
        awardedBiddingEntry,
        tradeInCalculation: _tradeInCalculation,
        handover,
        activities,
        lastActivity,
        identifier,
        frontPagePhotoSourceId,
        vehicleDiagramComments,
        ...fields
    } = values;

    const { targetHandoverDateTime, handoverLocation, personResponsible, vsaAttachment, ...handoverFields } = handover;
    const handoverLocationOption = handoverLocationOptions.find(option => option.value === handoverLocation);

    const handoverLocationField = {
        main: handoverLocationOption ? handoverLocationOption.value : defaultOtherLocationOption,
        other: !handoverLocationOption ? handoverLocation : '',
    };

    const handlingCharges = fields.handlingCharges ?? { value: 0, unit: SettingValueUnit.Amount };
    const margin = fields.margin ?? { value: 0, unit: SettingValueUnit.Amount };

    return {
        ...fields,
        handlingCharges,
        margin,
        salesConsultant: values.salesConsultant?.id || null,
        handover: {
            targetHandoverDate: targetHandoverDateTime as Date,
            targetHandoverTime: targetHandoverDateTime as Date,
            handoverLocationField: handoverLocation ? handoverLocationField : { main: '' },
            personResponsible: personResponsible?.id || '',
            targetHandoverDateTime,
            vsaAttachments: vsaAttachment ? [vsaAttachment] : [],
            ...handoverFields,
        },
        biddingSession: latestBiddingSession && {
            name: latestBiddingSession.sessionName,
            notes: latestBiddingSession.notes,
            urgent: latestBiddingSession.urgent,
            startPeriod: latestBiddingSession.startPeriod ? new Date(latestBiddingSession.startPeriod) : null,
            endPeriod: latestBiddingSession.endPeriod ? new Date(latestBiddingSession.endPeriod) : null,
        },
        // switch it with the original document
        frontPagePhoto: fields.photos.find(document => document.id === frontPagePhotoSourceId),
        // comments
        comments: vehicleDiagramComments.map(comment => ({
            id: comment.id,
            index: comment.index,
            comment: comment.comment,
            position: comment.position,
        })),
    };
};

const computePayload = (values: EditSaleTransactionFormValues): UpdateSaleTransactionMutationVariables['fields'] => {
    const { frontPagePhoto, photos, handover, biddingSession, comments, ...fields } = values;

    const {
        targetHandoverDate,
        targetHandoverTime,
        handoverLocationField,
        handoverLocation,
        targetHandoverDateTime,
        attachments,
        vsaAttachments,
        personResponsible,
        ...handoverFields
    } = handover;

    let targetHandoverDateTimeNew: Date = null;

    if (targetHandoverDate) {
        targetHandoverDateTimeNew = targetHandoverTime
            ? dayjs(targetHandoverDate)
                  .set('hour', dayjs(targetHandoverTime).hour())
                  .set('minute', dayjs(targetHandoverTime).minute())
                  .toDate()
            : targetHandoverDate;
    }

    return {
        handover: {
            targetHandoverDateTime: targetHandoverDateTimeNew,
            handoverLocation: handoverLocationField.other || handoverLocationField.main,
            personResponsible: personResponsible || null,
            ...handoverFields,
        },
        ...fields,
    };
};

const EditSaleTransaction = ({ saleTransaction, goToView }: EditSaleTransactionProps) => {
    const [updateSaleTransaction] = useUpdateSaleTransactionMutation();
    const [uploadHandoverAttachmentMutation] = useUploadSalesTransactionHandoverAttachmentMutation();
    const [removeHandoverAttachmentMutation] = useRemoveSalesTransactionHandoverAttachmentMutation();
    const [removeSalesTransactionPhotoMutation] = useRemoveSalesTransactionPhotoMutation();
    const [uploadSalesTransactionPhotoMutation] = useUploadSalesTransactionPhotoMutation();
    const [uploadVsaAttachmentMutation] = useUploadVsaAttachmentMutation();
    const [removeVsaAttachmentMutation] = useRemoveVsaAttachmentMutation();
    const [updateSalesTransactionFrontPagePhotoMutation] = useUpdateSalesTransactionFrontPagePhotoMutation();
    const [updateBiddingMutation] = useUpdateBiddingMutation();
    const [addSalesTransactionVehicleDiagram] = useAddSalesTransactionVehicleDiagramCommentMutation();
    const [removeSalesTransactionVehicleDiagram] = useRemoveSalesTransactionVehicleDiagramCommentMutation();

    const currentUser = useCurrentUser();

    const basicStyles = useBasicStyle();

    const initialFrontPagePhotoId = saleTransaction.frontPagePhotoSourceId;
    const initialPhotos = saleTransaction.photos;
    const initialAttachments = saleTransaction.handover.attachments;
    const initialVehicleDiagramComments = saleTransaction.vehicleDiagramComments;
    const initialVsaAttachments = saleTransaction.handover.vsaAttachment
        ? [saleTransaction.handover.vsaAttachment]
        : [];

    const onSubmit = useHandleError(
        async (values: EditSaleTransactionFormValues) => {
            const photosDiff = diffUploads(values.photos, initialPhotos);
            const attachmentsDiff = diffUploads(values.handover.attachments, initialAttachments);
            const vsaAttachmentsDiff = diffUploads(values.handover.vsaAttachments, initialVsaAttachments);
            const fields = computePayload(values);
            const { frontPagePhoto, biddingSession, comments } = values;

            await Promise.all([
                ...photosDiff.removedUploads.map(async file => {
                    await removeSalesTransactionPhotoMutation({
                        variables: { salesTransactionId: saleTransaction.id, uploadedFileId: file.id },
                    });
                }),
                ...attachmentsDiff.removedUploads.map(async file => {
                    await removeHandoverAttachmentMutation({
                        variables: { salesTransactionId: saleTransaction.id, uploadedFileId: file.id },
                    });
                }),
                ...vsaAttachmentsDiff.removedUploads.map(async file => {
                    await removeVsaAttachmentMutation({
                        variables: { salesTransactionId: saleTransaction.id, uploadedFileId: file.id },
                    });
                }),
                ...photosDiff.newUploads.map(async file => {
                    await uploadSalesTransactionPhotoMutation({
                        variables: {
                            salesTransactionId: saleTransaction.id,
                            file,
                            isFrontPagePhoto: frontPagePhoto === file,
                        },
                    });
                }),
                ...attachmentsDiff.newUploads.map(async file => {
                    await uploadHandoverAttachmentMutation({
                        variables: { salesTransactionId: saleTransaction.id, file },
                    });
                }),
                ...vsaAttachmentsDiff.newUploads.map(async file => {
                    await uploadVsaAttachmentMutation({
                        variables: { salesTransactionId: saleTransaction.id, file },
                    });
                }),
            ]);

            if (!(frontPagePhoto instanceof File) && frontPagePhoto?.id !== initialFrontPagePhotoId) {
                await updateSalesTransactionFrontPagePhotoMutation({
                    variables: { salesTransactionId: saleTransaction.id, frontPagePhotoId: frontPagePhoto?.id },
                });
            }

            if (
                saleTransaction.stage !== SaleTransactionStage.New &&
                saleTransaction.stage !== SaleTransactionStage.Completed &&
                saleTransaction.stage !== SaleTransactionStage.Closed &&
                saleTransaction.stage !== SaleTransactionStage.Cancelled
            ) {
                const { latestBiddingSession } = saleTransaction;

                await updateBiddingMutation({
                    variables: {
                        biddingId: latestBiddingSession.id,
                        fields: {
                            sessionName: biddingSession.name,
                            urgent: biddingSession.urgent,
                            startPeriod: biddingSession.startPeriod,
                            endPeriod: biddingSession.endPeriod,
                            notes: biddingSession.notes,
                        },
                    },
                });
            }

            await updateSaleTransaction({
                variables: { transactionId: saleTransaction.id, fields },
            });

            // Create / delete diagram comments
            const commentIds = comments.map(comment => comment.id);
            const removedArray = initialVehicleDiagramComments.filter(item => !commentIds.includes(item.id));

            for (const comment of removedArray) {
                // eslint-disable-next-line no-await-in-loop
                await removeSalesTransactionVehicleDiagram({
                    variables: {
                        salesTransactionId: saleTransaction.id,
                        vehicleDiagramCommentID: comment.id,
                    },
                });
            }

            const newArray = comments.filter(item => !item.id);
            for (const comment of newArray) {
                // eslint-disable-next-line no-await-in-loop
                await addSalesTransactionVehicleDiagram({
                    variables: {
                        fields: comment,
                        salesTransactionId: saleTransaction.id,
                    },
                });
            }

            goToView();
        },
        [saleTransaction, goToView, initialFrontPagePhotoId, initialPhotos]
    );

    const { t } = useTranslation(['biddingSessionsPage']);
    const formValidator = useMemo(
        () =>
            validators.compose(
                // validation for Admin,SalesManager,Purchaser
                validators.only(
                    () =>
                        currentUser.type === UserType.Admin ||
                        currentUser.type === UserType.SalesManager ||
                        currentUser.type === UserType.Purchaser,
                    validators.compose(
                        validators.requiredString('vehicle.number'),
                        validators.requiredNumber('vehicle.mileage'),
                        validators.requiredDate('vehicle.intendedDeregistrationDate'),
                        validators.requiredString('vehicle.make'),
                        validators.requiredString('vehicle.model'),
                        validators.requiredString('vehicle.primaryColour'),
                        validators.requiredNumber('vehicle.manufacturingYear'),
                        validators.requiredString('vehicle.engineNumber'),
                        validators.requiredString('vehicle.chassisNumber'),
                        validators.requiredString('vehicle.maximumPowerOutput'),
                        validators.requiredNumber('vehicle.openMarketValue'),
                        validators.requiredDate('vehicle.originalRegistrationDate'),
                        validators.requiredDate('vehicle.firstRegistrationDate'),
                        validators.requiredNumber('vehicle.transferCount'),
                        validators.requiredNumber('vehicle.actualARFPaid'),
                        validators.requiredDate('vehicle.coeExpiryDate'),
                        validators.requiredString('vehicle.coeCategory'),
                        validators.requiredNumber('vehicle.coePeriodYear'),
                        validators.requiredString('salesConsultant'),
                        validators.only(
                            ({ vehicle: { parfEligibility } }) => parfEligibility === true,
                            validators.compose(validators.requiredString('vehicle.parfEligibilityDate'))
                        ),
                        validators.compose(
                            validators.requiredNumber('handlingCharges.value'),
                            validators.requiredString('handlingCharges.unit')
                        ),
                        validators.compose(
                            validators.requiredNumber('margin.value'),
                            validators.requiredString('margin.unit')
                        ),

                        validators.only(
                            () =>
                                saleTransaction.stage === SaleTransactionStage.UpcomingBidding ||
                                saleTransaction.stage === SaleTransactionStage.New ||
                                saleTransaction.stage === SaleTransactionStage.BiddingOpen,
                            validators.compose(
                                validators.requiredString('biddingSession.name'),
                                validators.requiredDate(
                                    'biddingSession.startPeriod',
                                    t('biddingSessionsPage:session.invalidDate')
                                ),
                                validators.requiredDate(
                                    'biddingSession.endPeriod',
                                    t('biddingSessionsPage:session.invalidDate')
                                ),
                                validators.custom('biddingSession.endPeriod', (value, values) => {
                                    const startValue = values?.biddingSession?.startPeriod;

                                    if (!startValue || !value) {
                                        return null;
                                    }

                                    return dayjs(startValue).isBefore(dayjs(value))
                                        ? null
                                        : t('biddingSessionsPage:session.endDateAfterStartDate');
                                })
                            )
                        ),
                        validators.only(
                            () => saleTransaction.stage === SaleTransactionStage.BiddingOpen,
                            validators.compose(
                                validators.custom('biddingSession.endPeriod', (value, values) => {
                                    if (!dayjs().isBefore(dayjs(value))) {
                                        return t('biddingSessionsPage:session.invalidDate');
                                    }

                                    return null;
                                })
                            )
                        )
                    )
                ),

                validators.only(
                    () => saleTransaction.stage === SaleTransactionStage.ForAwarding,
                    validators.compose(
                        validators.only(
                            () =>
                                currentUser.type === UserType.Admin ||
                                currentUser.type === UserType.SalesManager ||
                                currentUser.type === UserType.Purchaser ||
                                currentUser.type === UserType.ValuationTeam ||
                                currentUser.type === UserType.Approver,
                            validators.compose(
                                // Currently PTI has no validation for personResponsible
                                // validators.requiredString('handover.personResponsible'),
                                validators.requiredString('handover.targetHandoverDate'),
                                validators.requiredString('handover.targetHandoverTime')
                            )
                        )

                        // Currently PTI has no validation for vehicleSalesAgreementNo
                        // but if needed in the future, it can be added here as well
                        /* validators.only(
                            () =>
                                currentUser.type === UserType.Admin ||
                                currentUser.type === UserType.SalesManager ||
                                currentUser.type === UserType.Purchaser ||
                                currentUser.type === UserType.Approver,
                            validators.compose(validators.requiredString('handover.vehicleSalesAgreementNo'))
                        ) */
                    )
                )
            ),
        [currentUser.type, saleTransaction.stage, t]
    );

    const validate = useValidator(formValidator);

    const handoverLocationOptions = useHandoverLocationOptions();
    const initialValues = useMemo(
        () => computeInitialValues(saleTransaction, handoverLocationOptions),
        [handoverLocationOptions, saleTransaction]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {() => (
                <Form>
                    <FileViewerProvider>
                        <EditSaleTransactionHeader goToView={goToView} />
                        <Box className={basicStyles.mainDesktop}>
                            <EditSaleTransactionBody saleTransaction={saleTransaction} />
                        </Box>
                    </FileViewerProvider>
                </Form>
            )}
        </Formik>
    );
};

export default EditSaleTransaction;
